import React, { useCallback, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import theme from '@/styles/theme';
import Message from '@/constant/message';
import showAlert, { hideAlert } from '@/components/common/alert';
import getHTMLContent from '@/utils/get-html-content';

interface ButtonProps {
  confirmButtonText?: string;
  confirmButtonColor?: 'purple' | 'red';
}

interface PromptProps {
  confirmButtonText?: string;
  confirmButtonColor?: string;
  disabled?: boolean;
}

const useAlert = () => {
  const history = useHistory();
  const { t } = useTranslation();

  const deleteWithInput = useCallback(async (content: JSX.Element) => {
    const result = await showAlert({
      customJSX: (
        content
      ),
      allowKeyPress: true, // default로 키 프레스가 막혀있음
      maxHeight: 360,
    });
    return result;
  }, []);

  /** 옵션이 있는 alert */
  const optionConfirm = useCallback(async (
    content: JSX.Element,
    buttons?: ButtonProps,
  ) => {
    if (!buttons) {
      buttons = {};
    }
    if (!buttons.confirmButtonColor) {
      buttons.confirmButtonColor = 'purple';
    }
    if (!buttons.confirmButtonText) {
      buttons.confirmButtonText = t(Message.확인) || '';
    }

    const confirmButtonColor = buttons.confirmButtonColor === 'purple' ? theme.color.purple : theme.color.red;
    const result = await showAlert({
      customJSX: (
        content
      ),
      maxHeight: 348,
      buttons: [{
        type: 'cancel',
        label: t(Message.닫기),
        width: '150px',
        color: '#4E4E4E',

      }, {
        type: 'confirmWithMessage',
        label: t(Message.시작),
        color: confirmButtonColor,
        width: '150px',
      }],
    });
    return result;
  }, [t]);

  /** alert만 버튼 색상 변경  */
  const alert = useCallback(async (msg: string, buttonText: string = t(Message.확인)) => {
    const result = await showAlert({
      customJSX: (
        <div>
          <CommonAlertDesc dangerouslySetInnerHTML={getHTMLContent(msg)} />
        </div>
      ),
      // desc: msg,
      buttons: [{
        type: 'confirm',
        label: buttonText,
        color: theme.color.purple,
        width: '80%',
      }],
    });

    return result;
  }, [t]);

  /** 메시지가 있는 알럿 */
  const alertWithMessage = useCallback(async (
    title: string,
    msg: string,
    buttonText: string = t(Message.확인),
  ) => {
    const result = await showAlert({
      title,
      desc: msg,
      buttons: [{
        type: 'alertWithMessage',
        label: buttonText,
        color: theme.color.purple,
        width: '150px',
      }],
    });

    return result;
  }, [t]);

  /** 중간에 색깔로 강조하고 싶은 메세지 있는 알럿 */
  // 번역할 때, 강조하고 싶은 부분에 em 넣어서 해야한다
  const colorWithMessage = useCallback(async (
    title: string,
    msg: string,
    secondMsg: string,
    thirdMsg?: string,
    buttonText: string = t(Message.확인),
  ) => {
    const result = await showAlert({
      customJSX: (
        <div>
          <Title dangerouslySetInnerHTML={getHTMLContent(title)} />
          <Desc dangerouslySetInnerHTML={getHTMLContent(msg)} />
          <Desc dangerouslySetInnerHTML={getHTMLContent(secondMsg)} />
          {thirdMsg
          && <Desc dangerouslySetInnerHTML={getHTMLContent(thirdMsg)} />}
        </div>
      ),
      buttons: [{
        type: 'alertWithMessage',
        label: buttonText,
        color: theme.color.purple,
        width: '314px',
      }],
    });

    return result;
  }, [t]);

  const confirm = useCallback(async (
    msg: string,
    buttons?: ButtonProps,
  ) => {
    if (!buttons) {
      buttons = {};
    }
    if (!buttons.confirmButtonColor) {
      buttons.confirmButtonColor = 'purple';
    }
    if (!buttons.confirmButtonText) {
      buttons.confirmButtonText = t(Message.확인) || '';
    }
    const confirmButtonColor = buttons.confirmButtonColor === 'purple' ? theme.color.purple : theme.color.red;
    const result = await showAlert({
      desc: msg,
      buttons: [{
        type: 'cancel',
        label: t(Message.취소),
        color: theme.color.disabled,
      }, {
        type: 'confirm',
        label: buttons.confirmButtonText,
        color: confirmButtonColor,
      }],
    });

    return result;
  }, [t]);

  /** 메시지가 있는 컨펌  */
  const confirmWithMessage = useCallback(async (
    title: string,
    msg: string,
    buttons?: ButtonProps,
  ) => {
    if (!buttons) {
      buttons = {};
    }
    if (!buttons.confirmButtonColor) {
      buttons.confirmButtonColor = 'purple';
    }
    if (!buttons.confirmButtonText) {
      buttons.confirmButtonText = t(Message.나가기) || '';
    }

    const confirmButtonColor = buttons.confirmButtonColor === 'purple' ? theme.color.purple : theme.color.warningRed;
    const result = await showAlert({
      customJSX: (
        <div>
          <Title dangerouslySetInnerHTML={getHTMLContent(title)} />
          <ConfirmDesc dangerouslySetInnerHTML={getHTMLContent(msg)} />
        </div>
      ),
      // title,
      // desc: msg,
      maxHeight: 260,
      buttons: [{
        type: 'cancel',
        label: t(Message.취소),
        width: '150px',
        color: '#4E4E4E',

      }, {
        type: 'confirmWithMessage',
        label: buttons.confirmButtonText,
        color: confirmButtonColor,
        width: '150px',
      }],
    });

    return result;
  }, [t]);

  const logoutConfirm = useCallback(async (
    msg: string,
    buttons?: ButtonProps,
  ) => {
    if (!buttons) {
      buttons = {};
    }
    // if (!buttons.confirmButtonColor) {
    //   buttons.confirmButtonColor = 'purple';
    // }
    if (!buttons.confirmButtonText) {
      buttons.confirmButtonText = t(Message.로그아웃) || '';
    }
    // const confirmButtonColor = buttons.confirmButtonColor === 'purple' ? theme.color.purple : theme.color.red;
    const result = await showAlert({
      desc: msg,
      buttons: [{
        type: 'cancel',
        label: t(Message.취소),
        color: theme.color.disabled,
        width: '100px',
      }, {
        type: 'confirm',
        label: buttons.confirmButtonText,
        color: theme.color.purple,
        width: '100px',
      }],
    });

    return result;
  }, [t]);

  const prompt = useCallback(async (
    msg: string,
    defaultValue: string,
    {
      confirmButtonColor = theme.color.purple,
      confirmButtonText = t(Message.확인) || '',
      disabled = false,
    }: PromptProps,
  ) => {
    let str = defaultValue;
    // eslint-disable-next-line react-perf/jsx-no-new-function-as-prop
    const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      str = e.currentTarget.value;
    };
    const result = await showAlert({
      desc: msg,
      customJSX: (
        <PromptInput
          disabled={disabled}
          defaultValue={str}
          spellCheck={false}
          onChange={onChange}
        />),
      buttons: [{
        type: 'cancel',
        label: t(Message.취소),
        color: theme.color.disabled,
      }, {
        type: 'confirm',
        label: confirmButtonText,
        color: confirmButtonColor,
      }],
      allowKeyPress: true,
    });
    if (result) {
      return str;
    }
    return null;
  }, [t]);

  const customAlert = useCallback(showAlert, []);

  useEffect(() => {
    hideAlert();
  }, [history]);

  return {
    alert,
    confirm,
    customAlert,
    prompt,
    logoutConfirm,
    confirmWithMessage,
    alertWithMessage,
    colorWithMessage,
    optionConfirm,
    deleteWithInput,
  };
};

export default useAlert;

// 위에 있던 스타일 아래로 이동
const PromptInput = styled.input`
  border: 1px solid ${theme.color.disabled};
  padding: 12px;
  font-size: 16px;
  border-radius: 5px;
  background-color: ${theme.color.white};
  outline: none;
  margin-top: 10px;
  min-width: 300px;

  &:focus {
    border-color: ${theme.color.skyblue};
  }

  &:disabled {
    background-color: ${theme.color.placeholder};
    color: ${theme.color.disabled};
  }
`;

const CommonAlertDesc = styled.div`
  font-family: ${theme.fontFamily.bold};
  font-size: 18px;
  line-height: 26px;
  text-align: center;
  margin-bottom: 20px;
  white-space:pre-wrap;
  padding: 0 51px;
  >em{
    color: ${theme.color.purple};
  }
`;

const Title = styled.div`
  font-family: ${theme.fontFamily.bold};
  font-size: 18px;
  line-height: 21px;
  text-align: center;
  margin-bottom: 20px;
`;

const Desc = styled.div`
  font-family: ${theme.fontFamily.regular};
  font-size: 14px;
  line-height: 20px;
  text-align: center;
`;

const ConfirmDesc = styled.div`
  padding-top: 12px;
  color: ${theme.color.black};
  font-family: ${theme.fontFamily.regular};
  font-size: 14px;
  line-height: 20px;
  white-space:pre-wrap;
  text-align: center;

  &.second {
    padding-top: 3px;
  }
  em {
    color: ${theme.color.warningRed};
    font-style: normal;
  }
`;
